import {Fragment, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {DRIVE_PAGES, TransactionsPage, PaymentHistoryPage} from '@app/routes';
import {driveState, useDriveStore} from './drive-store';
import {Navbar} from './navBar/navBar';
import {Sidebar} from './sideBar/sideBar';
import {menuConfig} from './sideBar/menuConfig';
import {useAccount} from 'wagmi';
import {fetchAllTransferedDocs} from './files/queries/use-paginated-entries';
import {DashboardSidenav} from '../ui/layout/dashboard-sidenav';
import {StaticPageTitle} from '@app/ui/utils/seo/static-page-title';
import {Base_url} from '@app/config';
import {TransactionsTable} from './files/table/transactions-table';
import {PaymentHistoryTable} from './files/table/payment-history-table';
import {MintedLayout} from './minted';

interface BaseLayoutProps {
  children: React.ReactNode;
  isConnected: any;
}

export function BaseLayout({children, isConnected}: BaseLayoutProps) {
  const {pathname} = useLocation();
  const activePage = useDriveStore(s => s.activePage);
  const [isMobileMode, setIsMobileMode] = useState(window.innerWidth < 768);
  const [shouldShowToggle, setShouldShowToggle] = useState(isMobileMode);
  const [showSideBar, setShowSideBar] = useState(true);
  const [showMobileSideBar, setShowMobileSideBar] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 800) {
      return JSON.parse(localStorage.getItem('showSideBar') || 'false');
    }
    return false;
  });
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const {address} = useAccount();
  const setDocuments = useDriveStore(s => s.setDocuments);

  // Store wallet address globally for API calls
  useEffect(() => {
    if (address) {
      (window as any).wagmiAddress = address;
    }
  }, [address]);

  // Fetch documents based on auth type
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const authType = localStorage.getItem('authType');
        const userEmail = localStorage.getItem('userEmail');

        if (address) {
          // Wallet authentication (existing - don't change)
          const docs = await fetchAllTransferedDocs(address);
          setDocuments(docs || { file_entries: [], template_data: [] });

          // Fetch and store party_id for wallet
          try {
            const partyIdResponse = await fetch(`${Base_url}party-id/${encodeURIComponent(address)}`);
            if (partyIdResponse.ok) {
              const partyData = await partyIdResponse.json();
              if (partyData.party_id) {
                localStorage.setItem('party_id', partyData.party_id);
                console.log('✅ Party ID stored for wallet:', partyData.party_id);
              }
            }
          } catch (err) {
            console.warn('⚠️ Could not fetch party_id for wallet:', err);
          }
        } else if (authType === 'gmail' && userEmail) {
          // Gmail authentication (new)
          console.log('Fetching documents for Gmail user:', userEmail);

          const response = await fetch(
            `${Base_url}file-entries-by-email/${encodeURIComponent(userEmail)}`,
            {
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
              },
            }
          );

          if (response.ok) {
            const docs = await response.json();
            console.log('Documents fetched:', docs);
            setDocuments(docs || { file_entries: [], template_data: [] });
          } else {
            console.error('Failed to fetch documents:', response.status);
            setDocuments({ file_entries: [], template_data: [] } as any);
          }

          // Fetch and store party_id for email (if not already stored)
          if (!localStorage.getItem('party_id')) {
            try {
              const partyIdResponse = await fetch(`${Base_url}party-id/${encodeURIComponent(userEmail)}`);
              if (partyIdResponse.ok) {
                const partyData = await partyIdResponse.json();
                if (partyData.party_id) {
                  localStorage.setItem('party_id', partyData.party_id);
                  console.log('✅ Party ID stored for email:', partyData.party_id);
                }
              }
            } catch (err) {
              console.warn('⚠️ Could not fetch party_id for email:', err);
            }
          }
        } else {
          // No auth - set empty
          setDocuments({ file_entries: [], template_data: [] } as any);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setDocuments({ file_entries: [], template_data: [] } as any);
      }
    };

    // Always fetch on mount or when deps change
    fetchDocuments();
  }, [address, setDocuments]);

  useEffect(() => {
    setShouldShowToggle(isMobileMode);
    if (isMobileMode) {
      setShowMobileSideBar(
        JSON.parse(localStorage.getItem('showSideBar') || 'false')
      );
    }
  }, [isMobileMode]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileMode(window.innerWidth < 800);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let page = DRIVE_PAGES.find(p => p.path === pathname);
    // Handle special routes
    if (pathname === '/transactions') {
      page = TransactionsPage;
    } else if (pathname === '/payment-history') {
      page = PaymentHistoryPage;
    }
    if (page) {
      driveState().setActivePage(page);
    }
  }, [pathname]);

  useEffect(() => {
    if (isMobileMode) {
      localStorage.setItem('showSideBar', JSON.stringify(showMobileSideBar));
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobileMode &&
        showMobileSideBar &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setShowMobileSideBar(false);
        setShowSideBar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMode, showMobileSideBar]);

  const handleClick = () => {
    if (showMobileSideBar) {
      setShowMobileSideBar(false);
      setShowSideBar(false);
    }
  };

  return (
    <Fragment>
      {activePage?.label && (
        <StaticPageTitle>{activePage.label}</StaticPageTitle>
      )}
      <div className="relative isolate dashboard-grid h-screen">
        <Navbar
          shouldShowToggle={shouldShowToggle}
          setShowSideBar={setShowSideBar}
          isMobileMode={isMobileMode}
          setShowMobileSideBar={setShowMobileSideBar}
        />

        {isConnected ? (
          showMobileSideBar ? (
            <>
              <div ref={sidebarRef}>
                <DashboardSidenav
                  position="left"
                  isMobileSideBar={true}
                  overlayPosition="overflow-hidden w-240 fixed"
                >
                  <Sidebar menu={menuConfig} handleClick={handleClick} />
                </DashboardSidenav>
              </div>
              <div
                className="bg-background/80 inset-0 z-10 h-full w-full fixed backdrop-blur-sm"
                aria-hidden="true"
                style={{opacity: 1}}
              ></div>
            </>
          ) : (
            <>
              {!isMobileMode && showSideBar && (
                <DashboardSidenav
                  position="left"
                  isMobileSideBar={false}
                  overlayPosition="overflow-hidden w-240"
                >
                  <Sidebar menu={menuConfig} handleClick={handleClick} />
                </DashboardSidenav>
              )}
              <div className="relative outline-none overflow-hidden dashboard-grid-content bg-[#f9fafb]">
                {pathname === '/minted' ? (
                  <MintedLayout />
                ) : pathname === '/transactions' ? (
                  <TransactionsTable />
                ) : pathname === '/payment-history' ? (
                  <PaymentHistoryTable />
                ) : (
                  <div className="stable-scrollbar h-full overflow-y-auto">
                    {children}
                  </div>
                )}
              </div>
            </>
          )
        ) : (
          <div className="relative outline-none overflow-y-auto stable-scrollbar dashboard-grid-content bg-[#f9fafb]">
            {children}
          </div>
        )}
      </div>
    </Fragment>
  );
}
